import { type FormEvent, useState } from "react";
import type { DisputeResponse } from "@hybrid-escrow/shared";
import { useDispute } from "../hooks/useDispute";
import { useDisputes } from "../hooks/useDisputes";
import { DisputeStatusBadge } from "../components/StatusBadge";
import { ErrorBanner, LoadingState } from "../components/States";
import { formatDateTime } from "../lib/format";

const STATUS_FILTERS: Array<{ label: string; value: DisputeResponse["status"] | "" }> = [
  { label: "All open cases", value: "" },
  { label: "Pending onchain", value: "PENDING_ONCHAIN" },
  { label: "Open", value: "OPEN" },
  { label: "Under review", value: "UNDER_REVIEW" },
  { label: "Resolved", value: "RESOLVED" },
  { label: "Rejected", value: "REJECTED" },
];

/**
 * Restricted offchain case-management view for MODERATOR/ADMIN. Lists disputes via
 * `GET /api/v1/disputes` and supports looking a specific case up by ID (e.g. from a support
 * ticket). Read-only by design — see the warning banner below for why.
 */
export function ModerationPage() {
  const [statusFilter, setStatusFilter] = useState<DisputeResponse["status"] | "">("");
  const [lookupId, setLookupId] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);

  const list = useDisputes(statusFilter ? { status: statusFilter, limit: 50 } : { limit: 50 });
  const { data: dispute, isLoading, isError, error } = useDispute(activeId ?? undefined);

  function handleLookup(e: FormEvent) {
    e.preventDefault();
    setActiveId(lookupId.trim() || null);
  }

  return (
    <div className="stack">
      <h1>Moderation</h1>

      <div className="alert alert--warning" role="alert">
        <strong>Offchain case management only.</strong>
        <p>
          This view lets staff read dispute details and prepare a recommendation. It cannot resolve
          a dispute. Onchain resolution requires a transaction signed by the configured arbitrator
          Safe/multisig — no action here bypasses that.
        </p>
      </div>

      <div className="card stack">
        <div className="row row--between">
          <h2>Cases</h2>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="status-filter">Filter by status</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DisputeResponse["status"] | "")}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {list.isLoading && <LoadingState label="Loading cases…" />}
        {list.isError && <ErrorBanner error={list.error} title="Couldn't load cases" />}
        {list.data && list.data.items.length === 0 && <p className="muted">No cases here.</p>}
        {list.data && list.data.items.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Case</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Opened</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((d) => (
                  <tr key={d.id}>
                    <td className="mono text-sm">{d.id.slice(0, 8)}…</td>
                    <td>{d.category}</td>
                    <td>
                      <DisputeStatusBadge status={d.status} />
                    </td>
                    <td>{formatDateTime(d.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setActiveId(d.id);
                          setLookupId(d.id);
                        }}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <form className="card row" onSubmit={handleLookup}>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="dispute-lookup">Or look up a case directly by ID</label>
          <input
            id="dispute-lookup"
            type="text"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            placeholder="e.g. from a support ticket"
          />
        </div>
        <button type="submit" className="btn btn-primary">
          Look up
        </button>
      </form>

      {activeId && isLoading && <LoadingState label="Loading case…" />}
      {activeId && isError && <ErrorBanner error={error} title="Couldn't load that dispute" />}

      {dispute && (
        <div className="card stack">
          <div className="row row--between">
            <h2>Case {dispute.id}</h2>
            <DisputeStatusBadge status={dispute.status} />
          </div>
          <div>
            <p className="text-sm muted">Order ID</p>
            <p className="mono text-sm">{dispute.orderId}</p>
          </div>
          <div>
            <p className="text-sm muted">Category</p>
            <p>{dispute.category}</p>
          </div>
          <div>
            <p className="text-sm muted">Buyer&apos;s claim</p>
            <p className="desc-full">{dispute.claim}</p>
          </div>
          <div>
            <p className="text-sm muted">Seller&apos;s response</p>
            <p className="desc-full">{dispute.sellerRebuttal ?? "No response yet."}</p>
          </div>
          <div>
            <p className="text-sm muted">Opened</p>
            <p>{formatDateTime(dispute.createdAt)}</p>
          </div>
          {dispute.resolvedAt && (
            <div>
              <p className="text-sm muted">Resolved</p>
              <p>{formatDateTime(dispute.resolvedAt)}</p>
            </div>
          )}
          <div className="alert alert--info" role="status">
            <p className="text-sm">
              To resolve: run <code>npm run arbitrate</code> from the repo with this dispute&apos;s
              ID and your decided split. See <code>scripts/resolve-dispute.md</code> for the exact
              command.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
